import { useEffect, useState } from "react";
import { AdminLayout } from "../../../components/admin/AdminLayout";
import { ForbiddenPanel } from "../../../components/admin/ForbiddenPanel";
import { authedFetch } from "../../../lib/api-client";
import { useT } from "../../../lib/use-t";
import { SCHRIJVEN_TASK_TYPES, SCHRIJVEN_TASK_LABELS } from "@a2/core";
import type { SchrijvenTaskType } from "@a2/core";

type VariantRow = {
  id: string;
  taskType: string;
  scenario: string;
  status: string;
  createdAt: string;
  createdBy: string | null;
};

const STATUS_COLOR: Record<string, string> = {
  live: "var(--pass)",
  ai_pending: "var(--warn)",
  retired: "var(--ink-faint)",
  curated: "var(--accent)",
};

export function AdminSchrijvenVariantsPage() {
  const t = useT();
  const [variants, setVariants] = useState<VariantRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [taskTypeFilter, setTaskTypeFilter] = useState<SchrijvenTaskType | "">("");
  const [statusFilter, setStatusFilter] = useState<string>("");

  function load() {
    setLoading(true);
    const params = new URLSearchParams();
    if (taskTypeFilter) params.set("taskType", taskTypeFilter);
    if (statusFilter) params.set("status", statusFilter);
    authedFetch(`/admin/schrijven/variants?${params.toString()}`)
      .then(async (r) => {
        if (r.status === 403) { setForbidden(true); return; }
        const body = await r.json();
        if (!body.ok) { setError(body.error?.message ?? "Fout"); return; }
        setVariants(body.data as VariantRow[]);
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : "Fout"))
      .finally(() => setLoading(false));
  }

  useEffect(load, [taskTypeFilter, statusFilter]);

  if (forbidden) return <AdminLayout><ForbiddenPanel /></AdminLayout>;

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-mono-badge text-xs uppercase tracking-wider text-[var(--ink-faint)]">
              {t("admin.schrijven.variantsEyebrow")}
            </p>
            <h1 className="text-2xl font-semibold tracking-tight text-[var(--ink)] mt-1">
              {t("admin.schrijven.variantsTitle")}
            </h1>
          </div>
          <a
            href="/admin/schrijven/variants/new"
            className="inline-flex h-9 items-center rounded-[var(--radius)] bg-[var(--accent)] px-4 text-sm font-medium text-white hover:bg-[var(--accent-hover)] transition-colors"
          >
            {t("admin.schrijven.newVariantButton")}
          </a>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3">
          <select
            value={taskTypeFilter}
            onChange={(e) => setTaskTypeFilter(e.target.value as SchrijvenTaskType | "")}
            className="rounded-md border border-[var(--border)] bg-[var(--panel)] px-3 py-1.5 text-sm text-[var(--ink)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--accent)]"
          >
            <option value="">{t("admin.schrijven.filterAllTypes")}</option>
            {SCHRIJVEN_TASK_TYPES.map((taskType) => (
              <option key={taskType} value={taskType}>{SCHRIJVEN_TASK_LABELS[taskType]}</option>
            ))}
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="rounded-md border border-[var(--border)] bg-[var(--panel)] px-3 py-1.5 text-sm text-[var(--ink)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--accent)]"
          >
            <option value="">{t("admin.schrijven.filterAllStatuses")}</option>
            <option value="curated">{t("admin.schrijven.statusCurated")}</option>
            <option value="ai_pending">{t("admin.schrijven.statusAiPending")}</option>
            <option value="live">{t("admin.schrijven.statusLive")}</option>
            <option value="retired">{t("admin.schrijven.statusRetired")}</option>
          </select>
        </div>

        {error ? (
          <p role="alert" className="text-sm text-[var(--fail)]">{error}</p>
        ) : loading ? (
          <p className="text-sm text-[var(--ink-faint)]">{t("admin.schrijven.variantsLoading")}</p>
        ) : variants.length === 0 ? (
          <p className="text-sm text-[var(--ink-muted)]">{t("admin.schrijven.variantsEmpty")}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="border-b border-[var(--border)] text-left">
                  <th className="pb-2 font-mono-badge text-xs uppercase tracking-wider text-[var(--ink-faint)] pr-4">{t("admin.schrijven.tableColType")}</th>
                  <th className="pb-2 font-mono-badge text-xs uppercase tracking-wider text-[var(--ink-faint)] pr-4">{t("admin.schrijven.tableColScenario")}</th>
                  <th className="pb-2 font-mono-badge text-xs uppercase tracking-wider text-[var(--ink-faint)] pr-4">{t("admin.schrijven.tableColStatus")}</th>
                  <th className="pb-2 font-mono-badge text-xs uppercase tracking-wider text-[var(--ink-faint)]">{t("admin.schrijven.tableColActions")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--border)]">
                {variants.map((v) => (
                  <tr key={v.id} className="hover:bg-[var(--panel-soft)] transition-colors">
                    <td className="py-3 pr-4 font-mono-badge text-xs text-[var(--ink-faint)] uppercase">
                      {v.taskType}
                    </td>
                    <td className="py-3 pr-4 text-[var(--ink-muted)] max-w-xs truncate">
                      {v.scenario}
                    </td>
                    <td className="py-3 pr-4">
                      <span
                        className="inline-flex items-center gap-1.5 font-mono-badge text-xs"
                        style={{ color: STATUS_COLOR[v.status] ?? "var(--ink-faint)" }}
                      >
                        <span className="w-1.5 h-1.5 rounded-full inline-block" style={{ background: "currentColor" }} />
                        {v.status === "curated"
                        ? t("admin.schrijven.statusCurated")
                        : v.status === "ai_pending"
                          ? t("admin.schrijven.statusAiPending")
                          : v.status === "live"
                            ? t("admin.schrijven.statusLive")
                            : v.status === "retired"
                              ? t("admin.schrijven.statusRetired")
                              : v.status}
                      </span>
                    </td>
                    <td className="py-3">
                      <a
                        href={`/admin/schrijven/variants/${v.id}`}
                        className="text-xs text-[var(--accent)] hover:underline"
                      >
                        {t("admin.schrijven.editAction")}
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
